import {Component} from 'panel';

import {registerMPElement} from '../../util/register-element';
import {onAnimationEnd, offAnimationEnd, TAB_KEY_CODE} from '../../util/dom';
import {getFocusableChildren, getActiveElement} from '../../util/focusable';

import template from './index.jade';

import css from './index.styl?inline';

export const VISIBILITY_OPEN = `open`;
export const VISIBILITY_OPENING = `opening`;
export const VISIBILITY_CLOSED = `closed`;
export const VISIBILITY_CLOSING = `closing`;

export const CLOSED_EVENT = `modal-closed`;
export const CLOSING_EVENT = `modal-closing`;

const MODAL_TYPE = {
  POPUP: `popup`,
  MODAL: `modal`,
};

const MODAL_VERTICAL_ALIGNMENT = {
  TOP: `top`,
  CENTER: `center`,
  BOTTOM: `bottom`,
};

export default registerMPElement(
  `mp-modal`,
  class extends Component {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        closeable: `boolean`,
        'grow-to-content': `boolean`,
        'hide-close-button': `boolean`,
        scrollable: `boolean`,
        'modal-type': {type: `string`, default: MODAL_TYPE.MODAL, enum: Object.values(MODAL_TYPE)},
        'modal-title': `string`,
        'modal-description': `string`,
        'not-fullscreen': `boolean`,
        open: `boolean`,
        'vertical-alignment': {
          type: `string`,
          default: MODAL_VERTICAL_ALIGNMENT.CENTER,
          enum: Object.values(MODAL_VERTICAL_ALIGNMENT),
        },
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        defaultState: {
          visibility: VISIBILITY_CLOSED,
        },
        helpers: {
          backdropClicked: () => {
            if (this.attr(`closeable`)) {
              this.close();
            }
          },
          closeClicked: () => {
            this.close();
          },
          getType: () => {
            return this.attr(`modal-type`);
          },
          getAlignmentConstants() {
            return {
              vertical: MODAL_VERTICAL_ALIGNMENT,
            };
          },
        },
      };
    }

    setDocumentScroll(enabled) {
      document.body.style.overflow = enabled ? `initial` : `hidden`;
    }

    close() {
      if (this.attr(`scrollable`)) {
        this.setDocumentScroll(true);
      }

      switch (this.state.visibility) {
        case VISIBILITY_CLOSED:
        case VISIBILITY_CLOSING:
          break;
        case VISIBILITY_OPENING:
          this.update({visibility: VISIBILITY_CLOSED});
          this.dispatchEvent(new CustomEvent(CLOSED_EVENT, {bubbles: true, composed: true}));
          break;
        case VISIBILITY_OPEN:
          this._pendingAnimations = [`fadeModalOut`];
          if (this.config.helpers.getType() === `modal`) {
            this._pendingAnimations.push(`fadeOverlayOut`);
          }
          this.update({visibility: VISIBILITY_CLOSING});
          this.dispatchEvent(new CustomEvent(CLOSING_EVENT, {bubbles: true, composed: true}));
          break;
      }
    }

    focusableChildren() {
      return Array.from(this.el.querySelectorAll(`slot`))
        .flatMap((n) => n.assignedNodes())
        .flatMap(getFocusableChildren);
    }

    open() {
      window.requestAnimationFrame(() => {
        const children = this.focusableChildren();
        if (children[0]) {
          children[0].focus();
        }
      });

      if (this.attr(`scrollable`)) {
        this.setDocumentScroll(false);
      }

      switch (this.state.visibility) {
        case VISIBILITY_OPEN:
        case VISIBILITY_OPENING:
          break;
        case VISIBILITY_CLOSING:
          this.update({visibility: VISIBILITY_OPEN});
          break;
        case VISIBILITY_CLOSED:
          this._pendingAnimations = [`fadeModalIn`];
          if (this.config.helpers.getType() === `modal`) {
            this._pendingAnimations.push(`fadeOverlayIn`);
          }
          this.update({visibility: VISIBILITY_OPENING});
          break;
      }
    }

    connectedCallback() {
      super.connectedCallback();

      // listen for escape keypress
      this.maybeCloseOnEscape = (ev) => {
        if (this.attr(`closeable`) && ev.keyCode === 27) {
          if (new Set([VISIBILITY_OPEN, VISIBILITY_OPENING]).has(this.state.visibility)) {
            ev.preventDefault();
          }

          if (!this._pendingAnimations || this._pendingAnimations.length === 0) {
            this.close();
          }
        }
      };
      document.body.addEventListener(`keydown`, this.maybeCloseOnEscape, true);

      this.trapTab = (ev) => {
        if (ev.keyCode === TAB_KEY_CODE && new Set([VISIBILITY_OPEN, VISIBILITY_OPENING]).has(this.state.visibility)) {
          const children = this.focusableChildren();
          // If there are no focusable children in the dialog, prevent tabbing through the rest
          // of the document before clearing focus on the dialog
          if (!children.length) {
            return ev.preventDefault();
          }

          const focusedItemIndex = children.indexOf(getActiveElement(document, children));

          if (ev.shiftKey && focusedItemIndex === 0) {
            children[children.length - 1].focus();
            ev.preventDefault();
          } else if (!ev.shiftKey && focusedItemIndex === children.length - 1) {
            children[0].focus();
            ev.preventDefault();
          }
        }
      };
      document.body.addEventListener(`keydown`, this.trapTab, true);

      // transition between states after animations complete
      this._onAnimationEnd = (ev) => {
        if (this._pendingAnimations.length === 0) {
          return;
        }
        this._pendingAnimations = this._pendingAnimations.filter((anim) => anim !== ev.animationName);
        if (this._pendingAnimations.length > 0) {
          return;
        }
        switch (this.state.visibility) {
          case VISIBILITY_OPENING:
            this.update({visibility: VISIBILITY_OPEN});
            this.dispatchEvent(new CustomEvent(`change`, {detail: {state: VISIBILITY_OPEN}}));
            break;
          case VISIBILITY_CLOSING:
            this.update({visibility: VISIBILITY_CLOSED});
            this.dispatchEvent(new CustomEvent(`change`, {detail: {state: VISIBILITY_CLOSED}}));
            this.dispatchEvent(new CustomEvent(CLOSED_EVENT, {bubbles: true, composed: true}));
            break;
        }
      };
      onAnimationEnd(this.el, this._onAnimationEnd);

      if (this.attr(`open`)) {
        this.open();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.body.removeEventListener(`keydown`, this.maybeCloseOnEscape, true);
      document.body.removeEventListener(`keydown`, this.trapTab, true);
      offAnimationEnd(this.el, this._onAnimationEnd);
      this.setDocumentScroll(true);
    }

    attributeChangedCallback(attr, newValue, oldValue) {
      super.attributeChangedCallback(attr, newValue, oldValue);
      if (this.initialized && attr === `open`) {
        if (this.attr(`open`)) {
          this.open();
        } else {
          this.close();
        }
      }
    }
  },
);
